// SkuSearchSelect — single-select searchable combobox for picking a SKU on a
// pricing line item. Replaces a native <select> that listed ~175 SKUs flat.
//
// Controlled: the parent owns the `skus` list and the selected `value` (skuId);
// only open/search/highlight/filter UI state lives here. Selection flows out via
// onChange(skuId), mapping to the existing updateLineItem(key,'skuId',value).
//
// Mirrors the proven dropdown mechanics in PeopleMultiSelect (onMouseDown
// before blur, blur-timeout close, Escape with stopPropagation so the
// surrounding Dialog stays open). Two things it does that PeopleMultiSelect
// doesn't, both load-bearing here:
//   1. Flip the dropdown ABOVE the trigger when there isn't room below — the
//      Edit-estimate Dialog is overflow-y-auto and the line-items list grows,
//      so a fixed downward dropdown would clip (a native <select> never did).
//   2. Keep an unrenderable value (inactive/seeded SKU) and show a fallback
//      label — never auto-clear it, or a re-quote silently loses a line.
//
// Cascading filter bar (top of the open panel): Category first, then that
// category's attribute filters (dependent, broad→narrow). Native <select>s so
// they're clip-immune inside the panel + Dialog overflow; a relatedTarget guard
// on the input blur keeps the panel open while a filter select has focus. The
// attribute field per category comes from inventory-table-config (mirror-safe) —
// see sku-picker-filters.ts.
//
// `layout="rail"` (opt-in; Receive delivery only): the open panel puts the
// category control in a LEFT RAIL (168px; GUTTER / INSULATION / OTHER groups from
// lib/sku-rail-groups.ts) instead of the category <select>; the right column keeps
// the attribute filter row + the listbox. Picked design:
// _parts-picker/2026-10-07-receive-sections/picks.json → j1 = B (parts/j1-B.html,
// root class `rb`). Default 'panel' is byte-identical to the pre-rail markup.

import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, X } from 'lucide-react';
import type { InventorySkuRow, SkuCategory, ProductLine } from '@gutterinv/shared';
import { cn } from '../lib/utils.js';
import {
  filterSkus,
  formatSkuLabel,
  resolveSelectedLabel,
} from '../lib/sku-search.js';
import { moveHighlight } from '../lib/combobox-nav.js';
import {
  applyPickerFilters,
  attributeFiltersForCategory,
  optionsForFilter,
} from '../lib/sku-picker-filters.js';
import { orderPickerCategories } from '../lib/sku-picker-order.js';
import { railGroups, RAIL_ROOT_CLASS } from '../lib/sku-rail-groups.js';

interface SkuSearchSelectProps {
  /** Selected skuId, '' when none. */
  value: string;
  /** Active SKUs to choose from (parent-owned query). */
  skus: InventorySkuRow[];
  /** All categories (ordered by displayOrder) for the cascade's first filter. */
  categories?: SkuCategory[];
  /** Job trade — hard-restricts + orders the category list (own priority → shared,
   *  other trade excluded). null/undefined (e.g. standalone calculator) → no filter. */
  productLine?: ProductLine | null;
  onChange: (skuId: string) => void;
  disabled?: boolean;
  placeholder?: string;
  id?: string;
  /** When true, focus + open the dropdown on mount. Set ONLY for a row the user
   *  just added via "Add item" so they can pick without an extra click. Never set
   *  on modal mount — the Edit-estimate Dialog deliberately prevents open-autofocus
   *  so an existing estimate can't have its rows' focus stolen / overwritten. */
  autoOpen?: boolean;
  /** 'panel' (default) = category <select> in the filter bar. 'rail' = left
   *  category rail (Receive delivery). See the header comment. */
  layout?: 'panel' | 'rail';
}

let comboSeq = 0;

export function SkuSearchSelect({
  value,
  skus,
  categories = [],
  productLine = null,
  onChange,
  disabled = false,
  placeholder = 'Select SKU…',
  id,
  autoOpen = false,
  layout = 'panel',
}: SkuSearchSelectProps) {
  const rail = layout === 'rail';
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [highlight, setHighlight] = useState(0);
  const [flipUp, setFlipUp] = useState(false);
  const [maxHeight, setMaxHeight] = useState(256);
  // Cascade filters (reset every open).
  const [categoryId, setCategoryId] = useState<string | null>(null);
  const [attrFilters, setAttrFilters] = useState<Record<string, string>>({});

  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const optionRefs = useRef<Array<HTMLButtonElement | null>>([]);
  // Stable listbox id for aria-controls / aria-activedescendant.
  const listboxId = useMemo(() => `sku-combo-${(comboSeq += 1)}`, []);

  // Resolve the CLOSED-trigger label against the FULL skus — so an already-saved line
  // whose category is now hard-restricted by productLine still shows its real label
  // (the productLine filter applies to the picker's option list, never to selections).
  const selectedLabel = resolveSelectedLabel(skus, value);

  // Hard-restrict by job product line: drop the other trade's priority categories from
  // BOTH the category dropdown and the selectable SKU list. null line → no restriction.
  const ordered = useMemo(
    () => orderPickerCategories(categories, productLine),
    [categories, productLine],
  );
  const visibleSkus = useMemo(
    () =>
      ordered && ordered.excludedNames.size
        ? skus.filter((s) => !ordered.excludedNames.has(s.categoryName))
        : skus,
    [skus, ordered],
  );

  // Pipeline: category + attribute filters, THEN free-text search. Runs over visibleSkus
  // (productLine-restricted) — selectedLabel above still uses the full list.
  const filtered = useMemo(
    () =>
      open ? filterSkus(applyPickerFilters(visibleSkus, categoryId, attrFilters), search) : [],
    [open, visibleSkus, categoryId, attrFilters, search],
  );

  const categoryName = useMemo(
    () => (categoryId ? (categories.find((c) => c.id === categoryId)?.name ?? null) : null),
    [categoryId, categories],
  );
  // SKUs in the chosen category (drives which attribute filters are worth showing).
  const categorySkus = useMemo(
    () => (categoryId ? visibleSkus.filter((s) => s.categoryId === categoryId) : []),
    [visibleSkus, categoryId],
  );
  const shownFilters = useMemo(
    () => (categoryName ? attributeFiltersForCategory(categoryName, categorySkus) : []),
    [categoryName, categorySkus],
  );
  const filtersActive = categoryId !== null || Object.keys(attrFilters).length > 0;
  // Rail groups (rail layout only) — drawn from the productLine-ordered categories
  // when restricted, else from all categories.
  const groups = useMemo(
    () =>
      rail ? railGroups(ordered ? [...ordered.priority, ...ordered.shared] : categories) : [],
    [rail, ordered, categories],
  );

  function pickCategory(id: string | null) {
    setCategoryId(id);
    setAttrFilters({});
    setHighlight(0);
  }

  function clearFilters() {
    setCategoryId(null);
    setAttrFilters({});
    setHighlight(0);
  }

  // Set one attribute filter, then PRUNE any other attr whose value is no longer
  // offered (keeps the cascade dead-end-free when an earlier pick changes).
  function updateAttr(label: string, val: string) {
    setAttrFilters((prev) => {
      const next: Record<string, string> = { ...prev };
      if (val) next[label] = val;
      else delete next[label];
      for (const other of Object.keys(next)) {
        if (other === label) continue;
        if (!optionsForFilter(skus, categoryId, next, other).includes(next[other])) delete next[other];
      }
      return next;
    });
    setHighlight(0);
  }

  function openList() {
    if (disabled || open) return;
    setSearch('');
    setCategoryId(null);
    setAttrFilters({});
    // Highlight the currently-selected row (or the top) when opening.
    const idx = value ? skus.findIndex((s) => s.skuId === value) : -1;
    setHighlight(idx >= 0 ? idx : 0);
    setOpen(true);
  }

  function close() {
    setOpen(false);
    setSearch('');
  }

  function pick(s: InventorySkuRow) {
    onChange(s.skuId);
    close();
    inputRef.current?.blur();
  }

  // On open: pick flip direction AND size the list to fill the available space
  // — down to just short of the bottom of the containing dialog (or the
  // viewport when not in one), so long SKU lists show as many rows as fit.
  useLayoutEffect(() => {
    if (!open) return;
    const el = inputRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    // A host may mark the region the panel must stay inside (e.g. the Receive
    // delivery dialog's BODY row, so the open list never covers the Cancel /
    // Receive footer — owner 2026-10-07); else the whole dialog; else the viewport.
    const dialog = el.closest('[data-sku-panel-bound]') ?? el.closest('[role="dialog"]');
    const bound = dialog
      ? dialog.getBoundingClientRect()
      : { top: 0, bottom: window.innerHeight };
    const MARGIN = 12;
    const below = bound.bottom - rect.bottom - MARGIN;
    const above = rect.top - bound.top - MARGIN;
    const flip = below < 200 && above > below;
    setFlipUp(flip);
    // Floor so it's never a sliver; otherwise fill the available side.
    setMaxHeight(Math.max(160, Math.floor(flip ? above : below)));
  }, [open]);

  // Keep the highlighted option scrolled into view as arrows move it.
  useEffect(() => {
    if (!open) return;
    optionRefs.current[highlight]?.scrollIntoView({ block: 'nearest' });
  }, [highlight, open]);

  // A row the user just added via "Add item" opens immediately so they can pick
  // without an extra click. Mount-only: focusing the input fires onFocus →
  // openList (the same path as a user Tab), which opens the panel with the
  // cascade filters reset/ready. Existing rows mount with autoOpen=false, and the
  // modal-mount inertness (Dialog onOpenAutoFocus prevented) is untouched.
  useEffect(() => {
    if (autoOpen) inputRef.current?.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Escape') {
      if (open) {
        // Close only the dropdown; don't let the surrounding Dialog close.
        e.stopPropagation();
        e.preventDefault();
        close();
        inputRef.current?.blur();
      }
      return; // when already closed, let Escape bubble (Dialog handles it)
    }
    if (!open) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') {
        e.preventDefault();
        openList();
      }
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((h) => moveHighlight(h, 1, filtered.length));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => moveHighlight(h, -1, filtered.length));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const s = filtered[highlight];
      if (s) pick(s);
    } else if (e.key === 'Tab') {
      close();
    }
  }

  const filterSelectClass =
    'h-8 max-w-[10rem] rounded-md border border-input bg-background px-2 text-xs text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

  // The panel body (filter bar + listbox). In the rail layout it sits in a right
  // column beside the rail; in the panel layout it is the panel itself (no wrapper
  // node — the default markup stays byte-identical).
  const panelBody = (
    <>
      {/* Cascade filter bar — native <select>s (clip-immune); the input's
          relatedTarget guard keeps this panel open while one has focus. */}
      <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-border p-2">
        {!rail && (
          <select
            aria-label="Filter by category"
            value={categoryId ?? ''}
            onChange={(e) => pickCategory(e.target.value || null)}
            className={filterSelectClass}
          >
            <option value="">All categories</option>
            {ordered ? (
              // Job-restricted: own-trade priority group, then shared "Other" group.
              // <optgroup> labels are inherently non-selectable — the visual divider.
              <>
                <optgroup label={productLine === 'gutter' ? 'Gutter' : 'Insulation'}>
                  {ordered.priority.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </optgroup>
                <optgroup label="Other">
                  {ordered.shared.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </optgroup>
              </>
            ) : (
              categories.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))
            )}
          </select>
        )}
        {shownFilters.map((label) => {
          const opts = optionsForFilter(skus, categoryId, attrFilters, label);
          return (
            <select
              key={label}
              aria-label={`Filter by ${label}`}
              value={attrFilters[label] ?? ''}
              onChange={(e) => updateAttr(label, e.target.value)}
              className={filterSelectClass}
            >
              <option value="">{label}: any</option>
              {opts.map((v) => (
                <option key={v} value={v}>
                  {v}
                </option>
              ))}
            </select>
          );
        })}
        {filtersActive && (
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={clearFilters}
            className="inline-flex items-center gap-1 rounded px-1.5 py-1 text-xs text-muted-foreground transition-colors hover:bg-accent/60 hover:text-foreground"
          >
            <X className="h-3 w-3" />
            Clear filters
          </button>
        )}
      </div>
      <ul
        ref={listRef}
        id={listboxId}
        role="listbox"
        onMouseDown={(e) => {
          // Grabbing the scrollbar (or clicking list chrome/gaps) targets the <ul>
          // itself and would blur the input → the blur-timeout would close the
          // dropdown mid-drag. preventDefault keeps the input focused; Chromium still
          // initiates the native scrollbar drag. Option buttons handle their own
          // mousedown, so only act when the target IS the list.
          if (e.target === e.currentTarget) e.preventDefault();
        }}
        className="min-h-0 flex-1 overflow-auto py-1"
      >
        {filtered.map((s, idx) => (
          <li key={s.skuId} role="option" aria-selected={s.skuId === value}>
            <button
              ref={(el) => {
                optionRefs.current[idx] = el;
              }}
              id={`${listboxId}-opt-${idx}`}
              type="button"
              onMouseDown={(e) => {
                // onMouseDown (not onClick) so it fires before the input blur.
                e.preventDefault();
                pick(s);
              }}
              onMouseEnter={() => setHighlight(idx)}
              className={cn(
                'flex w-full items-center px-2 py-1.5 text-left text-sm hover:bg-accent/40',
                idx === highlight && 'bg-accent/40',
                s.skuId === value && 'font-medium',
              )}
            >
              <span className="truncate">{formatSkuLabel(s)}</span>
            </button>
          </li>
        ))}
        {filtered.length === 0 && (
          <li className="px-2 py-2 text-sm text-muted-foreground">
            {search ? `No SKUs match “${search}”` : 'No SKUs match these filters'}
          </li>
        )}
      </ul>
    </>
  );

  return (
    <div ref={rootRef} className="relative flex-1">
      <input
        ref={inputRef}
        id={id}
        type="text"
        role="combobox"
        aria-expanded={open}
        aria-controls={listboxId}
        aria-autocomplete="list"
        aria-activedescendant={
          open && filtered[highlight] ? `${listboxId}-opt-${highlight}` : undefined
        }
        readOnly={!open}
        disabled={disabled}
        value={open ? search : selectedLabel}
        placeholder={placeholder}
        onMouseDown={openList}
        onFocus={openList}
        onChange={(e) => {
          setSearch(e.target.value);
          setHighlight(0);
          setOpen(true);
        }}
        onKeyDown={onKeyDown}
        onBlur={(e) => {
          // Keep the panel open while focus moves to a filter <select> inside this
          // component (relatedTarget is in the root). Otherwise delay-close so a
          // click on an option (onMouseDown) registers first.
          if (rootRef.current?.contains(e.relatedTarget as Node | null)) return;
          window.setTimeout(() => setOpen(false), 120);
        }}
        className="flex h-9 w-full appearance-none truncate rounded-md border border-input bg-background px-3 pr-9 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
      />
      <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-taupe" />
      {open && (
        <div
          style={{ maxHeight }}
          className={cn(
            rail
              ? 'absolute z-20 flex w-full min-w-[24rem] flex-row rounded-md border border-border bg-card shadow-md'
              : 'absolute z-20 flex w-full min-w-[24rem] flex-col rounded-md border border-border bg-card shadow-md',
            flipUp ? 'bottom-full mb-1' : 'top-full mt-1',
          )}
        >
          {rail && (
            /* Left category rail (picked j1-B): hairline above each group, eyebrow
               group label, entries = hover tint / selected = primary tint + semibold.
               onMouseDown preventDefault keeps the input focused (no blur-close). */
            <div
              className={cn(RAIL_ROOT_CLASS, 'w-[168px] shrink-0 overflow-y-auto border-r border-border px-2 py-2 text-[12.5px]')}
              role="group"
              aria-label="Categories"
            >
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => pickCategory(null)}
                className={cn(
                  'block w-full rounded-md px-2 py-1.5 text-left leading-[15px] transition-colors hover:bg-accent/40',
                  categoryId === null && 'bg-primary/10 font-semibold',
                )}
              >
                All categories
              </button>
              {groups.map((g) => (
                <div key={g.label}>
                  <div className="eyebrow mt-2.5 border-t border-border px-2 pb-1 pt-3.5">{g.label}</div>
                  {g.items.map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => pickCategory(c.id)}
                      className={cn(
                        'block w-full truncate rounded-md px-2 py-1.5 text-left leading-[15px] transition-colors hover:bg-accent/40',
                        categoryId === c.id && 'bg-primary/10 font-semibold',
                      )}
                    >
                      {c.name}
                    </button>
                  ))}
                </div>
              ))}
            </div>
          )}
          {rail ? (
            /* pl-3 = breathing space between the rail's hairline and the body (owner 2026-10-07). */
            <div className="flex min-h-0 min-w-0 flex-1 flex-col pl-3">{panelBody}</div>
          ) : (
            panelBody
          )}
        </div>
      )}
    </div>
  );
}
